'use client';

import dynamic from 'next/dynamic';
import Link from '~/(tenant)/_components/tenant-link';
import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import type { JWTUserData } from '@kit/supabase/types';
import { Button } from '@kit/ui/button';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import { UserAccountDropdown } from './user-account-dropdown';
import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';

const ModeToggle = dynamic(() =>
  import('@kit/ui/mode-toggle').then((mod) => ({
    default: mod.ModeToggle,
  })),
);

const MobileModeToggle = dynamic(() =>
  import('@kit/ui/mobile-mode-toggle').then((mod) => ({
    default: mod.MobileModeToggle,
  })),
);

const paths = {
  home: pathsConfig.app.home,
  bookCourt: '/book',
};

const features = {
  enableThemeToggle: featuresFlagConfig.enableThemeToggle,
};

export function SiteHeaderAccountSection({ hideBookingAction = false }: { hideBookingAction?: boolean } = {}) {
  const session = useSession();
  const userRole = useUserRole(session.data?.user?.id);
  const teamAccount = useTeamAccount(session.data?.user?.id);
  const signOut = useSignOut({
    onSuccess: () => window.location.reload(),
  });

  // Convert session user to JWTUserData format
  const jwtUserData = useMemo((): JWTUserData | null => {
    const user = session.data?.user;
    if (!user) return null;

    // User type from getSession doesn't include aal/amr, but JWTUserData requires them
    // These are typically available from the JWT claims, so we provide safe defaults
    const userWithExtras = user as typeof user & { aal?: string; amr?: Array<{ method: string; timestamp: number }> };

    return {
      id: user.id,
      email: user.email ?? '',
      phone: user.phone ?? '',
      is_anonymous: user.is_anonymous ?? false,
      aal: (userWithExtras.aal as 'aal1' | 'aal2') ?? 'aal1',
      amr: userWithExtras.amr ?? [],
      app_metadata: user.app_metadata ?? {},
      user_metadata: user.user_metadata ?? {},
    };
  }, [session.data?.user]);

  // If user is authenticated
  if (session.data && jwtUserData) {
    const role = userRole.data || 'guest';

    // Owner/Member roles - show admin access
    if (role === 'owner' || role === 'member') {
    return (
        <div className={'animate-in fade-in flex gap-x-2.5 duration-500'}>
          <div className={'hidden md:flex'}>
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <div className={'flex gap-x-2.5'}>
            {/* Book Court button - visible on all screen sizes as priority */}
            {!hideBookingAction && <Button asChild variant={'outline'} className={`bg-transparent text-sm px-4 py-1.5 rounded-full`}>
              <Link href={paths.bookCourt}>
                <Trans i18nKey={'common:bookNow'} />
              </Link>
            </Button>}

            <Button asChild variant={'outline'} className={`hidden md:flex text-sm px-4 py-1.5 rounded-full`}>
              <Link href={teamAccount.data?.slug ? `/home/${teamAccount.data.slug}/dashboard` : '/home/settings'}>
                <Trans i18nKey={'common:adminPanel'} defaults="Admin Panel" />
              </Link>
            </Button>

      <PersonalAccountDropdown
        showProfileName={false}
        paths={paths}
        features={features}
        user={jwtUserData}
        signOutRequested={() => signOut.mutateAsync()}
        className='z-50'
      />
          </div>
        </div>
    );
  }

    // User role - show profile only (no admin access)
    if (role === 'user') {
      return (
        <div className={'animate-in fade-in flex gap-x-2.5 duration-500'}>
          <div className={'hidden md:flex'}>
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          {!hideBookingAction && <div className='flex gap-x-2.5'>
            {/* Book Court button - visible on all screen sizes as priority */}
            <Button asChild variant={'outline'} className={`bg-transparent text-sm px-4 py-1.5 rounded-full`}>
              <Link href={paths.bookCourt}>
                <Trans i18nKey={'common:bookNow'} />
              </Link>
            </Button>
          </div>}

          <UserAccountDropdown
            showProfileName={false}
            features={features}
            user={jwtUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className='z-50'
          />
        </div>
      );
    }
  }

  // Guest users - show auth buttons
  return <AuthButtons hideBookingAction={hideBookingAction} />;
}

function AuthButtons({ hideBookingAction }: { hideBookingAction: boolean }) {
  return (
    <div className={'animate-in fade-in flex gap-x-2.5 duration-500'}>
      {!hideBookingAction && <div className='flex gap-x-2.5'>
        {/* Book Court button - visible on all screen sizes as priority */}
        <Button asChild variant={'outline'} className={`bg-transparent text-sm px-4 py-1.5 rounded-full`}>
          <Link href={paths.bookCourt}>
            <Trans i18nKey={'common:bookNow'} />
          </Link>
        </Button>
      </div>}

      <div className={'hidden md:flex'}>
        <If condition={features.enableThemeToggle}>
          <ModeToggle />
        </If>
      </div>

      <div className={'md:hidden'}>
        <If condition={features.enableThemeToggle}>
          <MobileModeToggle />
        </If>
      </div>

      {/* Use <a> tag for hard navigation to avoid hydration mismatch between route groups */}
      <div className={'flex gap-x-2.5'}>
        <Button
          asChild
          className={`bg-secondary text-primary hover:bg-secondary/90 text-sm px-5 py-1.5 rounded-full`}
          variant={'default'}
        >
          <a href={pathsConfig.auth.signIn}>
            <Trans i18nKey={'auth:signIn'} defaults="Sign In" />
          </a>
        </Button>
      </div>
    </div>
  );
}

function useSession() {
  const client = useSupabase();

  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();

      return data.session;
    },
  });
}

function useUserRole(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['user-role', userId],
    queryFn: async () => {
      if (!userId) return 'guest';
      
      try {
        // Check if user has account membership (owner/member)
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select('account_role')
          .eq('user_id', userId);
        
        // If user has any membership with owner or member role, they have admin access
        if (memberships && memberships.length > 0) {
          if (memberships.some(m => m.account_role === 'owner')) {
            return 'owner';
          }
          return 'member';
        }

        // If authenticated but no account membership, they're a 'user'
        return 'user';
      } catch {
        return 'guest';
      }
    },
    enabled: !!userId,
  });
}

function useTeamAccount(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['team-account', userId],
    queryFn: async () => {
      if (!userId) return null;
      
      try {
        // Get user's team account membership
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select(`
            account_id,
            accounts!accounts_memberships_account_id_fkey!inner (
              id,
              slug,
              name,
              is_personal_account
            )
          `)
          .eq('user_id', userId)
          .eq('accounts.is_personal_account', false);
        
        // Return the first team account
        if (memberships && memberships.length > 0 && memberships[0]?.accounts) {
          return memberships[0].accounts;
        }
        
        return null;
      } catch {
        return null;
      }
    },
    enabled: !!userId,
  });
}
